import React from 'react';
import PageHero from '../../components/ui-shree/PageHero';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import WaterIsLife from '../../components/Story/WaterIsLife';
import LivingVessel from './LivingVessel';
import ProtocolOfHarmony from '../../components/Philosophy/ProtocolOfHarmony';
import { BRAND } from '../../lib/constants';

const CHAPTERS = [
  {
    no: 'I',
    title: 'The naming',
    body:
      'Shreesavaa, an old word folded twice — “Shree”, that quiet light reserved for what is sacred, and “Savaa”, a vow whispered into water before it is poured. We did not choose the name. The name found us, the way ingredients find a formula.',
  },
  {
    no: 'II',
    title: 'The first laboratory',
    body:
      'A stone room in Heavener, Oklahoma, January light, three brass funnels. We were trying to make something that did not yet exist: a luxury house with a verifiable conscience. We weighed sea-buckthorn by hand and wrote each batch into a notebook bound in linen.',
  },
  {
    no: 'III',
    title: 'The chain as memory',
    body:
      'When the notebook overflowed, we asked: what if the ledger could not be torn out? We placed our provenance on chain — soil to skin, harvest to hand — so that nothing could be quietly rewritten. Memory, made incorruptible. A house, given a soul.',
  },
  {
    no: 'IV',
    title: 'The ritual returns',
    body:
      'Skincare, at its highest, is not maintenance. It is correspondence. Every morning, every evening, you are answering the body. We make formulas worthy of that answer, and we mark each one with the Seva token, so devotion becomes its own currency.',
  },
  {
    no: 'V',
    title: 'The vow at the drain',
    body:
      'The ritual does not end where the bottle empties. What you wash away travels — through pipe, soil, river, sea — and meets a billion small mouths. We refuse harsh synthetics so that what we leave behind is food, not a problem. Water is life. We feed it.',
  },
];

const StoryPage = () => (
  <div data-testid="story-page">
    <PageHero
      overline="The Digital Soul"
      title={<>A house, given a <em className="text-[#90B0BF]">soul</em>.</>}
      subtitle="The story of Shreesavaa, told slowly. Four chapters on naming, making, remembering, and returning."
      testId="story-hero"
    />

    <section className="px-6 md:px-12 pb-24">
      <div className="max-w-3xl mx-auto">
        {CHAPTERS.map((c, i) => (
          <article
            key={c.no}
            data-testid={`story-chapter-${i}`}
            className={`py-16 ${i !== CHAPTERS.length - 1 ? 'border-b border-[#C1D7E2]' : ''}`}
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-3">
              Chapter {c.no}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#7C9CB1] font-light leading-[1.05] mb-8">
              {c.title}
            </h2>
            <p className="font-serif italic text-xl md:text-2xl text-[#7C9CB1]/90 leading-relaxed">
              {c.body}
            </p>
          </article>
        ))}
      </div>
    </section>

    {/* Living Vessel — 3D scrollytelling that mirrors the four chapters */}
    <LivingVessel />

    {/* Pull quote */}
    <section
      data-testid="story-pull-quote"
      className="px-6 md:px-12 py-32 bg-[#7C9CB1] text-[#F4F8F9]"
    >
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-serif text-3xl md:text-5xl leading-[1.15] font-light italic">
          &ldquo;The skin keeps a private record of the lives we have lived.
          Shreesavaa simply makes that record beautiful, and impossible to forge.&rdquo;
        </p>
        <p className="mt-10 font-mono text-[10px] tracking-[0.35em] uppercase text-[#F4F8F9]/80">
          — Founder&rsquo;s note · MMXXV
        </p>
      </div>
    </section>

    {/* The Sanatana Economy — water-is-life manifesto */}
    <WaterIsLife testId="story-water-is-life" />

    {/* The Protocol of Harmony — cooperative chemistry as philosophy */}
    <ProtocolOfHarmony testId="story-protocol-of-harmony" />

    {/* Atelier image */}
    <section className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9]">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <SectionLabel testId="atelier-label">The Atelier</SectionLabel>
          <h3 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-6">
            One soil, one breath.
          </h3>
          <p className="text-[#7C9CB1]/85 font-light leading-relaxed text-lg max-w-xl">
            Heavener, Oklahoma — the Ouachita foothills, slow water, slower
            light. Our ingredients travel only as far as they must — sometimes
            in linen, sometimes in cold steel, always with their name attached.
          </p>
          <p
            data-testid="story-origin-chip"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1]/80 border border-[#C1D7E2] px-3 py-1.5 bg-[#F4F8F9]"
          >
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF5A]"
            />
            Bottled in Heavener, Oklahoma
          </p>
          <div className="mt-8 border-t border-[#C1D7E2] pt-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
              Write to the house
            </p>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="story-house-email"
              className="font-serif text-2xl text-[#7C9CB1] hover:text-[#90B0BF] transition-colors"
            >
              {BRAND.email}
            </a>
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/4465831/pexels-photo-4465831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200"
              alt="Apothecary atelier"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default StoryPage;
